import React from 'react';
import { Dialog, DialogContent, DialogActions, Button, Box, Typography } from '@ellucian/react-design-system/core';
import SuccessIcon from '../../icons/success.svg';

interface InstallConfirmationDialogProps {
    open: boolean;
    onClose: () => void;
    onDone: () => void;
}

const InstallConfirmationDialog: React.FC<InstallConfirmationDialogProps> = ({ open, onClose, onDone }) => {
    const handleClose = () => {
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={(event: any, reason: string) => {
                // Only allow closing via the title bar close button
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
                handleClose();
            }}
            disableEscapeKeyDown // Prevent closing with Escape key
            showTitleBarClose={true} // Enable the built-in close button
            maxWidth={false}
            PaperProps={{
                sx: {
                    display: 'flex',
                    width: { xs: 'calc(100% - 32px)', sm: '648px' },
                    maxWidth: { xs: 'calc(100% - 32px)', sm: '648px' },
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    borderRadius: '4px',
                    boxShadow: '0px 8px 32px 0px rgba(0,0,0,0.16)',
                    flexDirection: 'column',
                    background: '#fff',
                    p: { xs: '8px', sm: '16px' },
                    margin: { xs: '16px', sm: 'auto' },
                    position: 'relative',
                }
            }}
            BackdropProps={{
                sx: {
                    background: 'rgba(0, 0, 0, 0.30) !important',
                }
            }}
        >
            <Box sx={{ width: '100%', pr: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 2 }, pb: 0 }}>
                <Typography
                    variant="h2"
                    sx={{
                        fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                        color: 'var(--Neutral-neutral-600, #151618)',
                        fontSize: '24px',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: '110%', // 26.4px
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        m: 0,
                        p: 0,
                        textAlign: 'left',
                    }}
                >
                    Request Submitted
                </Typography>
            </Box>
            <DialogContent sx={{ p: 0, textAlign: 'center' }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        alignSelf: 'stretch',
                        minHeight: '60px',
                        padding: '4px',
                        gap: '6px',
                        background: 'var(--Neutral-neutral-100, #fff)',
                        boxSizing: 'border-box',
                        pt: 5,
                        mb: 6,
                    }}
                >
                    <Box sx={{ width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                        <img
                            src={SuccessIcon}
                            alt="Success"
                            style={{ width: 60, height: 60, display: 'block' }}
                        />
                    </Box>
                    <Typography
                        variant="h4"
                        sx={{
                            color: 'var(--Neutral-neutral-600, #151618)',
                            textAlign: 'center',
                            fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                            fontSize: '16px',
                            fontStyle: 'normal',
                            fontWeight: 700,
                            lineHeight: '125%', // 20px
                            letterSpacing: '0.08px',
                            mb: 0,
                        }}
                    >
                        Installation Started Successfully
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-end', px: '32px', gap: 2 }}>
                <Button
                    onClick={onDone}
                    color="secondary"
                    sx={{
                        fontFamily: 'Poppins, Montserrat, Aptos, Roboto, "Century Gothic", Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif',
                        color: 'var(--Interactive-Blue-cta-blue-600, #026BC8)',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: '110%', // 17.6px
                        textTransform: 'capitalize',
                    }}
                >
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InstallConfirmationDialog;
